import { useCallback, useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"

import type { Route } from "@/api/models"

import { SidebarTrigger } from "@/components/ui/sidebar"
import { filterModuleRoutes } from "@/contexts/module-route-utils"
import { useRoutes } from "@/contexts/use-routes"
import { LargeLogo } from "@/customer/large-logo"
import { useGetTrainingModules } from "@/features/training/hooks/use-get-training-modules"

import { getDynamicChatRoute } from "./dynamic-chat-routes"
import { useHeaderRightSlot } from "./header-right-slot"
import { ModuleGallery } from "./module-gallery"
import { findParentModuleRoute } from "./module-route"

// Order of modules in the dropdown (best-effort: only applied if present)
const MODULE_DROPDOWN_ROUTE_ID_ORDER = [
  "open", // AI Explorer
  "advantum", // Advantum
  "workflows", // Flows
  "ai-parser", // AI Parser
  "studio_ai", // Studio AI
  "video-insight", // AI Video Insight
  "ai-portfolio", // AI Portfolio
  "slide-sense", // Slide Sense
  "segment-analysis", // Segment Analysis
  "local-ai-ambassador", // Local AI Lead (route id preserved for DB compatibility)
  "admin-dashboard", // Admin Dashboard
  // AI Training is appended to the end of the dropdown if enabled
] as const

const MODULE_DROPDOWN_PREFERRED_INDEX_BY_ID = new Map<string, number>(
  MODULE_DROPDOWN_ROUTE_ID_ORDER.map((id, index) => [id, index])
)

function orderRoutesForModuleDropdown(routes: Route[]): Route[] {
  const buckets = MODULE_DROPDOWN_ROUTE_ID_ORDER.map(() => [] as Route[])
  const remaining: Route[] = []

  for (const route of routes) {
    const index = MODULE_DROPDOWN_PREFERRED_INDEX_BY_ID.get(route.id)
    if (index === undefined) {
      remaining.push(route)
      continue
    }
    buckets[index].push(route)
  }

  return dedupeRoutesForDropdown([...buckets.flat(), ...remaining])
}

/** Drop duplicate module entries (same id or path) from Cosmos config. */
function dedupeRoutesForDropdown(routes: Route[]): Route[] {
  const seenIds = new Set<string>()
  const seenPaths = new Set<string>()
  return routes.filter((route) => {
    if (seenIds.has(route.id) || seenPaths.has(route.path)) {
      return false
    }
    seenIds.add(route.id)
    seenPaths.add(route.path)
    return true
  })
}

/**
 * Match a pathname against a route path pattern
 * Supports simple :param placeholders
 */
function matchRoutePath(pathname: string, routePath: string): boolean {
  if (pathname === routePath) return true

  const pathParts = pathname.split("/").filter(Boolean)
  const routeParts = routePath.split("/").filter(Boolean)

  if (pathParts.length !== routeParts.length) return false

  return routeParts.every((part, i) => {
    if (part.startsWith(":")) return true // :param matches anything
    return part === pathParts[i]
  })
}

type HeaderProps = {
  /**
   * Whether the current route renders a left rail (app/module sidebar). When
   * false — the rail-less modules (admin-dashboard, ai-portfolio,
   * segment-analysis, local-ai-lead, studio-ai, …) — the Header surfaces a
   * logo→home so every module keeps a way home. Defaults to true.
   */
  hasLeftRail?: boolean
}

export function Header({ hasLeftRail = true }: HeaderProps) {
  const location = useLocation()
  const rightSlot = useHeaderRightSlot()
  const navigate = useNavigate()
  const [activeLink, setActiveLink] = useState("")
  const { t, i18n } = useTranslation()
  const { routes, trainingEnabled, trainingModules: expectedTrainingModules } = useRoutes()
  const trainingModulesQuery = useGetTrainingModules()

  // Filter out projects and workflow-editor (only show workflows landing page)
  const filteredRoutes = useMemo(
    () =>
      filterModuleRoutes(routes).filter(
        (route) =>
          route.enabled &&
          route.id !== "projects" &&
          route.id !== "workflow-editor" &&
          route.id !== "documents"
      ),
    [routes]
  )

  // Best-effort ordering for the module dropdown:
  // - Place known route IDs in a preferred order (if present)
  // - Keep any other customer-specific routes in their config order
  const orderedRoutes = useMemo(
    () => orderRoutesForModuleDropdown(filteredRoutes),
    [filteredRoutes]
  )

  const userTrainingModules = trainingModulesQuery.data?.modules ?? []

  const expectedSectionIds = expectedTrainingModules.flatMap((module) =>
    module.sections.map((section) => section.id)
  )
  const statusMap = new Map(
    userTrainingModules
      .filter((module) => !!module.module_id)
      .map((module) => [module.module_id as string, module.status] as const)
  )

  const hasIncompleteTraining =
    trainingEnabled &&
    trainingModulesQuery.isSuccess &&
    expectedSectionIds.some((id) => statusMap.get(id) !== "completed")

  const showIncompleteTrainingBadge =
    hasIncompleteTraining && !location.pathname.startsWith("/training")

  const incompleteTrainingLabel = t("training.incompleteBadgeLabel", {
    defaultValue: "Training not completed",
  })

  const routePathToName: { [key: string]: string } = routes.reduce(
    (acc, route) => ({
      ...acc,
      [route.path]: route.name,
    }),
    {
      "/training": "AI Training",
      "/flows": "Flows",
      "/documents": "Documents",
    }
  )

  const mapPathNameToActiveLink = (pathname: string) => {
    if (pathname.startsWith("/documents")) {
      return t("documents.moduleName", { defaultValue: "Documents" })
    }

    // First try exact match
    if (routePathToName[pathname]) {
      return routePathToName[pathname]
    }

    // Handle all training sub-routes (e.g., /training/ai-explorer/getting-started)
    if (pathname.startsWith("/training")) {
      return "AI Training"
    }
    // Handle workflows routes (landing page and editor)
    if (pathname.startsWith("/flows")) {
      return "Flows"
    }
    // Handle AI Portfolio sub-routes (e.g., /ai-portfolio/dashboard)
    if (pathname.startsWith("/ai-portfolio")) {
      const portfolioRoute = routes.find((r) => r.id === "ai-portfolio")
      return portfolioRoute?.name || "AI Portfolio"
    }
    // Handle Segment Analysis routes
    if (pathname.startsWith("/segment-analysis")) {
      const segmentRoute = routes.find((r) => r.id === "segment-analysis")
      return segmentRoute?.name || "Segment Analysis"
    }
    // Handle Tender module routes (landing and case pages). The legacy /anbud
    // prefix still resolves for tenants whose route config mounts the module
    // there, and during the client-side redirect of old bookmarks/links.
    if (pathname.startsWith("/tender") || pathname.startsWith("/anbud")) {
      const tenderRoute = routes.find((r) => r.id === "tender")
      return tenderRoute?.name || "Tender"
    }
    // Try parameterized route matching against all routes
    const matchedRoute = routes.find((route) => matchRoutePath(pathname, route.path))
    if (matchedRoute) {
      return matchedRoute.name
    }

    // Handle project chat routes - these use "open" (Explorer)
    if (pathname.match(/^\/projects\/[^/]+\/chats\/[^/]+$/)) {
      const explorerRoute = routes.find((route) => route.id === "open")
      return explorerRoute?.name || "AI Explorer"
    }

    // Handle dynamic chat routes by checking the first path segment
    const pathSegments = pathname.split("/").filter(Boolean)
    if (pathSegments.includes("chat") && pathSegments.length >= 2) {
      // Try to find route for the base path
      const basePath = pathSegments[0]
      const baseRoute = routes.find((r) => r.path === `/${basePath}`)
      if (baseRoute) return baseRoute.name

      // Map known dynamic chat routes (e.g. /chat/:id → Explorer) to their
      // canonical path via the shared DYNAMIC_CHAT_ROUTES table.
      const mappedPath = getDynamicChatRoute(basePath)?.path
      if (mappedPath && routePathToName[mappedPath]) {
        return routePathToName[mappedPath]
      }
    }

    const parentModule = findParentModuleRoute(pathname, routes)
    if (parentModule) return parentModule.name

    // Default fallback
    return routes.length > 0 ? routes[0].name : ""
  }

  // Set initial active link
  useEffect(() => {
    setActiveLink(mapPathNameToActiveLink(location.pathname))
  }, [routes]) // Run when routes are fetched

  // Update active link when pathname changes
  useEffect(() => {
    setActiveLink(mapPathNameToActiveLink(location.pathname))
  }, [location.pathname]) // Dependency on location.pathname to run when it changes

  const getInfoText = useCallback(
    (info_text: string | { [key: string]: string } | undefined) => {
      if (!info_text) return ""
      if (typeof info_text === "string") {
        // Check if it's a translation key (contains a dot)
        if (info_text.includes(".")) {
          return t(info_text)
        }
        return info_text
      }

      // At this point, info_text is of type { [key: string]: string }
      const infoTextByLanguage = info_text
      return infoTextByLanguage[i18n.language] || infoTextByLanguage.sv || ""
    },
    [t, i18n.language]
  )

  return (
    <header
      className="bg-sidebar sticky top-0 z-50 flex h-16 w-full shrink-0 items-center justify-between gap-4 px-2 transition-[width,height] ease-linear"
      data-testid="main-header"
    >
      <section className="flex min-w-0 flex-1 items-center gap-2">
        <div className="flex items-center gap-3">
          {/* Mobile sidebar toggle — only meaningful when a left rail exists.
              Rail-less modules have no sidebar to open, so omit it. */}
          {hasLeftRail && (
            <SidebarTrigger className="size-10 shrink-0 rounded-full lg:hidden [&>svg]:size-5" />
          )}
          {/* Rail-less modules render no sidebar (and thus no logo→home). Surface
              the brand logo here so every module has a consistent way home.
              Hidden on phones (<md) where the switcher pill alone fits the row;
              home stays reachable there via the switcher gallery. */}
          {!hasLeftRail && (
            <button
              aria-label={t("pages.home", { defaultValue: "Home" })}
              className="hidden shrink-0 items-center rounded-md transition-opacity hover:opacity-80 md:flex"
              data-testid="header-logo-home"
              type="button"
              onClick={() => navigate("/")}
            >
              <LargeLogo className="h-8 w-auto max-w-[150px]" />
            </button>
          )}
          <div>
            <ModuleGallery
              activeLink={activeLink}
              getInfoText={getInfoText}
              hasIncompleteTraining={showIncompleteTrainingBadge}
              incompleteTrainingLabel={incompleteTrainingLabel}
              routes={orderedRoutes}
              trainingEnabled={trainingEnabled}
              onNavigate={navigate}
            />
          </div>
        </div>
      </section>
      <div className="ml-auto flex shrink-0 items-center gap-2" data-testid="header-right-slot">
        {rightSlot}
      </div>
    </header>
  )
}
